import React, { useCallback, useEffect, useRef, useState } from 'react';
import Markdown from '../Markdown';
import api from '../../lib/api';
import useMarkdownImages from '../../hooks/useMarkdownImages';
import type { TaskComment } from '../../types/taskComment';

// 今天写的只显示时分，更早的带上日期 —— 右栏窄，能省的字都省掉。
function stamp(iso: string): string {
  const date = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  const clock = `${pad(date.getHours())}:${pad(date.getMinutes())}`;
  const today = new Date();
  const sameDay =
    date.getFullYear() === today.getFullYear() &&
    date.getMonth() === today.getMonth() &&
    date.getDate() === today.getDate();

  return sameDay ? clock : `${date.getMonth() + 1}月${date.getDate()}日 ${clock}`;
}

type RowProps = {
  comment: TaskComment;
  onSave: (id: number, bodyMd: string) => Promise<void>;
  onDelete: (id: number) => Promise<void>;
};

const Row: React.FC<RowProps> = ({ comment, onSave, onDelete }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(comment.body_md);
  const [confirming, setConfirming] = useState(false);
  // 编辑态自己的错误位。不复用外层那个：外层那个报的是列表级失败（取不到、
  // 删不掉），跟「这一条正在编辑时图片没传上去」不是一件事，混在一处会让人
  // 分不清是哪条留言出的问题。
  const [error, setError] = useState<string | null>(null);
  const draftRef = useRef<HTMLTextAreaElement>(null);

  // 编辑已有留言时也能粘图。taskId 直接取 comment.task_id —— presenter 里有。
  const {
    takeFiles,
    uploading,
    handlers: imageHandlers,
  } = useMarkdownImages({
    upload: (file: File) => api.taskImages.create(comment.task_id, file),
    setValue: setDraft,
    textareaRef: draftRef,
    onError: setError,
  });

  // 服务端值变了（保存成功后的重取）跟着重置
  useEffect(() => {
    setDraft(comment.body_md);
  }, [comment.id, comment.body_md]);

  const commit = async () => {
    // 上传中不提交：占位串还在正文里，这时存下去等于把「![上传中…1]()」存进留言。
    if (uploading > 0) return;

    const next = draft.trim();
    // 空正文存不进去（模型 validates :body_md, presence: true）。与其让它
    // 报错，不如回滚 —— 想删有旁边的删除按钮。
    if (!next) {
      setDraft(comment.body_md);
      setEditing(false);
      return;
    }
    if (next !== comment.body_md) await onSave(comment.id, next);
    setEditing(false);
  };

  const isAgent = comment.author_kind === 'agent';

  // AI 留言走虚线框 + 暖底（todo.warn / todo.warnLine）—— 虚线加暖底
  // 已经在说「这是 agent 放进来的东西」。不开新 token。
  const shell = isAgent
    ? 'my-2 rounded-lg border border-dashed border-todo-warnLine bg-todo-warn px-[11px] py-[9px]'
    : 'border-t border-todo-line py-[9px] first:border-t-0';

  return (
    <div className={shell}>
      <div className="mb-1 flex items-center gap-2 text-[11px] text-todo-muted">
        {isAgent && (
          <span className="rounded border border-todo-warnLine px-1 font-mono text-[10px] font-semibold leading-[15px] text-todo-star">
            AI
          </span>
        )}
        <span className="flex-1 tabular-nums">
          {stamp(comment.created_at)}
          {comment.edited && <span className="ml-1.5">已编辑</span>}
        </span>

        {/* 常显，不用 opacity-0 group-hover —— 留言是内容不是列表项，
            而且这个组件在移动端也用，触屏没有 hover（spec §4.2）。 */}
        {!editing && !confirming && (
          <>
            {/* AI 写的只给删，不给改 —— 改过的 agent 留言两边都不是，
                author_kind 就成了一个不再为真的字段。真正的执行处在服务端
                （TaskCommentsController#update），这里只是不把按钮摆出来。 */}
            {!isAgent && (
              <button
                type="button"
                onClick={() => setEditing(true)}
                className="px-0.5 hover:text-todo-accent"
              >
                编辑
              </button>
            )}
            <button
              type="button"
              onClick={() => setConfirming(true)}
              className="px-0.5 hover:text-todo-urgent"
            >
              删除
            </button>
          </>
        )}

        {/* 内联二次确认：删错一条长留言不可逆，值得问一句（照 DetailPane
            底部那个模式，不引组件库、不弹窗）。 */}
        {confirming && (
          <>
            <span className="text-todo-ink2">删这条？</span>
            <button
              type="button"
              onClick={() => onDelete(comment.id)}
              className="px-0.5 text-todo-urgent hover:underline"
            >
              删除
            </button>
            <button
              type="button"
              onClick={() => setConfirming(false)}
              className="px-0.5 hover:underline"
            >
              取消
            </button>
          </>
        )}
      </div>

      {editing ? (
        <div>
          <textarea
            ref={draftRef}
            value={draft}
            autoFocus
            onChange={(event) => setDraft(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Escape') {
                setDraft(comment.body_md);
                setEditing(false);
                setError(null);
              }
              if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) {
                event.preventDefault();
                void commit();
              }
            }}
            className="min-h-[72px] w-full resize-y rounded border border-todo-line bg-todo-card p-2 text-[13px] leading-relaxed text-todo-ink outline-none"
            {...imageHandlers}
          />
          <div className="mt-1 flex items-center gap-2 text-[12px]">
            <button
              type="button"
              onClick={() => void commit()}
              disabled={uploading > 0}
              className="rounded-md bg-todo-accent px-2 py-0.5 text-white disabled:opacity-50"
            >
              保存
            </button>
            <button
              type="button"
              onClick={() => {
                setDraft(comment.body_md);
                setEditing(false);
                setError(null);
              }}
              className="text-todo-muted hover:underline"
            >
              取消
            </button>
            {/* 移动端没有「粘贴截图」这个动作，但能从相册选 */}
            <label className="cursor-pointer text-todo-muted hover:text-todo-accent">
              插入图片
              <input
                type="file"
                accept="image/*"
                multiple
                className="hidden"
                onChange={(event) => {
                  takeFiles(event.target.files);
                  // 不清空的话同一张图连续选第二次不会再触发 onChange
                  event.target.value = '';
                }}
              />
            </label>
            {uploading > 0 && <span className="text-todo-muted">上传中…</span>}
          </div>
          {error && <div className="mt-1 text-[12px] text-todo-urgent">{error}</div>}
        </div>
      ) : (
        <Markdown className="text-[13px] text-todo-ink">{comment.body_md}</Markdown>
      )}
    </div>
  );
};

// 骨架照 EntryList：自己按 task_id 取数（不进 Inertia props），换任务时重置
// 本地态。卡片外观与那份保持一致，右栏几张卡片看起来才是同一个人写的。
const CommentList: React.FC<{ taskId: number; onChanged: () => void }> = ({ taskId, onChanged }) => {
  const [comments, setComments] = useState<TaskComment[]>([]);
  const [draft, setDraft] = useState('');
  const [error, setError] = useState<string | null>(null);
  const draftRef = useRef<HTMLTextAreaElement>(null);

  const load = useCallback(() => {
    let cancelled = false;

    api.comments
      .list(taskId)
      .then((rows) => {
        if (!cancelled) setComments(rows as TaskComment[]);
      })
      .catch(() => {
        // 留言取不到就不显示这一段，不打断详情栏其余部分（照 EntryList）
        if (!cancelled) setComments([]);
      });

    return () => {
      cancelled = true;
    };
  }, [taskId]);

  useEffect(() => load(), [load]);

  useEffect(() => {
    setDraft('');
    setError(null);
  }, [taskId]);

  const { takeFiles, uploading, handlers: imageHandlers } = useMarkdownImages({
    upload: (file: File) => api.taskImages.create(taskId, file),
    setValue: setDraft,
    textareaRef: draftRef,
    onError: setError,
  });

  const submit = async () => {
    const body = draft.trim();
    if (!body) return;

    setError(null);
    try {
      await api.comments.create(taskId, body);
      setDraft('');
      load();
      // 中栏徽标读的是 Inertia props 里的 comments_count，要重取
      onChanged();
    } catch (e) {
      setError(e instanceof Error ? e.message : '发不出去');
    }
  };

  const save = async (id: number, bodyMd: string) => {
    setError(null);
    try {
      await api.comments.update(id, bodyMd);
      load();
    } catch (e) {
      setError(e instanceof Error ? e.message : '改不了');
    }
  };

  const remove = async (id: number) => {
    setError(null);
    try {
      await api.comments.destroy(id);
      load();
      onChanged();
    } catch (e) {
      setError(e instanceof Error ? e.message : '删不掉');
    }
  };

  const agentCount = comments.filter((comment) => comment.author_kind === 'agent').length;

  return (
    <div className="mb-[9px] rounded-[9px] border border-todo-line bg-todo-card shadow-sm">
      <div className="px-[13px] pb-[11px] pt-[9px]">
        <h4 className="mb-1.5 flex items-baseline text-[11px] font-medium tracking-wide text-todo-muted">
          <span className="flex-1">留言{comments.length > 0 && ` · ${comments.length} 条`}</span>
          {/* 上限是硬的：满了 task_comment_create 直接报错，逼你先删几条。
              10 这个数在这里是硬编码的第二份（模型里那份是
              TaskComment::AGENT_COMMENT_LIMIT）—— 它变的频率约等于零。 */}
          {agentCount > 0 && <span className="tabular-nums">AI {agentCount} / 10</span>}
        </h4>

        {comments.map((comment) => (
          <Row key={comment.id} comment={comment} onSave={save} onDelete={remove} />
        ))}

        <div className={comments.length > 0 ? 'mt-2 border-t border-todo-line pt-2' : ''}>
          <textarea
            ref={draftRef}
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            onKeyDown={(event) => {
              // 裸 Enter 是换行 —— markdown 要写列表和代码块，回车不能是提交。
              if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) {
                event.preventDefault();
                void submit();
              }
            }}
            {...imageHandlers}
            placeholder="写点什么（支持 markdown，⌘/Ctrl + Enter 发出）"
            className="min-h-[54px] w-full resize-y border-0 bg-transparent text-[13px] leading-relaxed text-todo-ink outline-none placeholder:text-todo-muted"
          />
          <div className="mt-1 flex items-center gap-2 text-[12px]">
            {/* 移动端没有「粘贴截图」这个动作，但能从相册选 */}
            <label className="cursor-pointer text-todo-muted hover:text-todo-accent">
              插入图片
              <input
                type="file"
                accept="image/*"
                multiple
                className="hidden"
                onChange={(event) => {
                  takeFiles(event.target.files);
                  // 不清空的话同一张图连续选第二次不会再触发 onChange
                  event.target.value = '';
                }}
              />
            </label>

            {uploading > 0 && <span className="text-todo-muted">上传中…</span>}

            {draft.trim() && (
              <button
                type="button"
                onClick={() => void submit()}
                disabled={uploading > 0}
                className="ml-auto rounded-md bg-todo-accent px-2 py-1 text-white disabled:opacity-50"
              >
                留言
              </button>
            )}
          </div>
        </div>

        {error && <div className="mt-1.5 text-[12px] text-todo-urgent">{error}</div>}
      </div>
    </div>
  );
};

export default CommentList;
